import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Input,
  InputNumber,
  Message,
  Modal,
  Pagination,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  type TableColumnProps,
} from "@arco-design/web-react";
import { Copy, Eye, RotateCcw, Search } from "lucide-react";
import QRCode from "react-qr-code";

import { useCurrentAuthUser } from "../../auth";
import { PermissionGate } from "../../../lib/auth";
import { CARD_PAYMENT_RECEIVE_BANKS, PAYMENT_VALIDATION_PERMISSIONS } from "../config";
import {
  useBankCardCollectionRuntime,
  useCardPaymentRuntime,
  usePaymentValidationRuntime,
} from "../runtime";
import {
  formatPaymentAmount,
  formatPaymentStatus,
  refundableAmount,
} from "../service";
import type {
  BankCardCollectionOrder,
  CardPaymentRecordItem,
  PaymentRecord,
  PaymentRecordDetail,
  QrCollectionOrder,
} from "../types";
import "./paymentValidation.css";

/** 收付款验证页面主体。 */
export function PaymentValidationFeaturePage() {
  return (
    <main className="payment-validation-page">
      <Tabs defaultActiveTab="qr" destroyOnHide>
        <Tabs.TabPane key="qr" title="二维码收款">
          <QrCollectionValidationPane />
        </Tabs.TabPane>
        <Tabs.TabPane key="bank-account" title="银行卡号收款">
          <BankCardCollectionValidationPane />
        </Tabs.TabPane>
        <Tabs.TabPane key="card" title="卡支付">
          <CardPaymentValidationPane />
        </Tabs.TabPane>
      </Tabs>
    </main>
  );
}

/** 渲染二维码收款查询、流水、详情与退款交互。 */
function QrCollectionValidationPane() {
  const runtime = usePaymentValidationRuntime();
  const currentUserQuery = useCurrentAuthUser();

  const columns: TableColumnProps<PaymentRecord>[] = [
    {
      title: "付款流水号",
      dataIndex: "provider_payment_no",
      width: 230,
    },
    {
      title: "交易方式",
      render: (_, item) => <TransactionMethodTag record={item} />,
      width: 150,
    },
    {
      title: "付款金额",
      render: (_, item) => `${Number(item.amount).toFixed(2)} 元`,
      width: 130,
    },
    {
      title: "已退金额",
      render: (_, item) => `${Number(item.refunded_amount).toFixed(2)} 元`,
      width: 130,
    },
    {
      title: "状态",
      render: (_, item) => (
        <Tag color={statusColor(item.status)}>{formatPaymentStatus(item.status)}</Tag>
      ),
      width: 110,
    },
    {
      title: "创建时间",
      render: (_, item) => formatDateTime(item.created_at),
      width: 180,
    },
    {
      title: "操作",
      fixed: "right",
      width: 160,
      render: (_, item) => (
        <Space size={2}>
          <Button
            size="small"
            type="text"
            icon={<Eye size={15} />}
            style={{ padding: "0 4px" }}
            onClick={() => void runtime.openDetail(item)}
          >
            详细
          </Button>
          <PermissionGate
            user={currentUserQuery.data}
            permission={PAYMENT_VALIDATION_PERMISSIONS.refund}
          >
            <Button
              size="small"
              type="text"
              status="danger"
              icon={<RotateCcw size={15} />}
              style={{ padding: "0 4px" }}
              disabled={
                item.record_type === "refund" ||
                refundableAmount(item.amount, item.refunded_amount) <= 0
              }
              onClick={() => runtime.openRefund(item)}
            >
              退款
            </Button>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <>
      <section className="payment-validation-search" aria-label="二维码收款查询">
        <Input
          aria-label="团编号"
          allowClear
          placeholder="请输入团编号"
          value={runtime.teamId}
          onChange={runtime.setTeamId}
          onPressEnter={() => void runtime.handleSearch()}
        />
        <PermissionGate
          user={currentUserQuery.data}
          permission={PAYMENT_VALIDATION_PERMISSIONS.create}
          fallback={<Button disabled>查询</Button>}
        >
          <Button
            type="primary"
            icon={<Search size={16} />}
            loading={runtime.loading}
            onClick={() => void runtime.handleSearch()}
          >
            查询
          </Button>
        </PermissionGate>
      </section>

      {runtime.errorMessage ? (
        <div className="payment-validation-alert">
          <Alert type="error" content={runtime.errorMessage} />
        </div>
      ) : null}

      {runtime.result ? (
        <>
          <section className="payment-validation-summary" aria-label="收款订单摘要">
            <div className="payment-validation-amounts">
              <div className="payment-validation-amounts__meta">
                <div>
                  <span>团编号</span>
                  <strong>{runtime.result.order.team_id}</strong>
                </div>
                <div>
                  <span>订单状态</span>
                  <Tag color={statusColor(runtime.result.order.status)}>
                    {formatPaymentStatus(runtime.result.order.status)}
                  </Tag>
                </div>
                <div>
                  <span>商户订单号</span>
                  <strong>{runtime.result.order.merchant_order_id}</strong>
                </div>
              </div>
              <div className="payment-validation-amounts__money" aria-label="收款金额对照">
                <div className="payment-validation-amount payment-validation-amount--expected">
                  <span>应收款</span>
                  <strong>{formatPaymentAmount(runtime.result.order.expected_amount)}</strong>
                </div>
                <div className="payment-validation-amount payment-validation-amount--actual">
                  <span>实际收款</span>
                  <strong>{formatPaymentAmount(runtime.result.order.actual_amount)}</strong>
                </div>
                <div className="payment-validation-amount payment-validation-amount--unpaid">
                  <span>未收款</span>
                  <strong>{formatPaymentAmount(runtime.result.order.unpaid_amount)}</strong>
                </div>
              </div>
            </div>
            <QrCollectionInstrument order={runtime.result.order} />
          </section>

          <section className="payment-validation-records" aria-label="付款记录">
            <div className="payment-validation-records__header">
              <Typography.Title heading={6}>付款记录</Typography.Title>
              <Typography.Text type="secondary">
                共 {runtime.result.payments.total} 条
              </Typography.Text>
            </div>
            <Table<PaymentRecord>
              rowKey="id"
              columns={columns}
              data={runtime.result.payments.items ?? []}
              pagination={false}
              scroll={{ x: 1090 }}
              noDataElement="暂无付款记录"
            />
            {runtime.result.payments.total > runtime.pageSize ? (
              <div className="payment-validation-pagination">
                <Pagination
                  current={runtime.page}
                  pageSize={runtime.pageSize}
                  total={runtime.result.payments.total}
                  onChange={(nextPage) => void runtime.handlePageChange(nextPage)}
                />
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      <Drawer
        title={
          runtime.detail?.payment.record_type === "refund" ? "退款详情" : "付款详情"
        }
        aria-label={
          runtime.detail?.payment.record_type === "refund" ? "退款详情" : "付款详情"
        }
        width="min(560px, 100vw)"
        visible={runtime.detailVisible}
        footer={
          <Button long onClick={runtime.closeDetail}>
            关闭
          </Button>
        }
        onCancel={runtime.closeDetail}
      >
        {runtime.detailLoading ? (
          <Spin block />
        ) : runtime.detail ? (
          <PaymentLedgerDetailView detail={runtime.detail} />
        ) : null}
      </Drawer>

      <Modal
        title="提交退款"
        style={{ width: "min(520px, calc(100vw - 24px))" }}
        visible={Boolean(runtime.refundPayment)}
        okText="确认退款"
        cancelText="取消"
        confirmLoading={runtime.refundLoading}
        onOk={() => void runtime.submitRefund()}
        onCancel={runtime.closeRefund}
      >
        {runtime.refundPayment ? (
          <div className="payment-validation-refund">
            <div>
              可退金额：
              <strong>
                {formatPaymentAmount(
                  refundableAmount(
                    runtime.refundPayment.amount,
                    runtime.refundPayment.refunded_amount,
                  ),
                )}
              </strong>
            </div>
            <InputNumber
              aria-label="退款金额"
              min={0.01}
              precision={2}
              step={0.01}
              value={runtime.refundAmount}
              onChange={runtime.setRefundAmount}
              prefix="¥"
              style={{ width: "100%" }}
            />
            {runtime.refundErrorMessage ? (
              <Alert type="error" content={runtime.refundErrorMessage} />
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

/** 渲染银行卡号收款查询、开卡进度、流水、详情与退款交互。 */
function BankCardCollectionValidationPane() {
  const runtime = useBankCardCollectionRuntime();
  const currentUserQuery = useCurrentAuthUser();

  const columns: TableColumnProps<PaymentRecord>[] = [
    {
      title: "付款流水号",
      dataIndex: "provider_payment_no",
      width: 230,
    },
    {
      title: "交易方式",
      render: (_, item) => <TransactionMethodTag record={item} />,
      width: 150,
    },
    {
      title: "付款金额",
      render: (_, item) => `${Number(item.amount).toFixed(2)} 元`,
      width: 130,
    },
    {
      title: "已退金额",
      render: (_, item) => `${Number(item.refunded_amount).toFixed(2)} 元`,
      width: 130,
    },
    {
      title: "状态",
      render: (_, item) => (
        <Tag color={statusColor(item.status)}>{formatPaymentStatus(item.status)}</Tag>
      ),
      width: 110,
    },
    {
      title: "创建时间",
      render: (_, item) => formatDateTime(item.created_at),
      width: 180,
    },
    {
      title: "操作",
      fixed: "right",
      width: 160,
      render: (_, item) => (
        <Space size={2}>
          <Button
            size="small"
            type="text"
            icon={<Eye size={15} />}
            style={{ padding: "0 4px" }}
            onClick={() => void runtime.openDetail(item)}
          >
            详细
          </Button>
          <PermissionGate
            user={currentUserQuery.data}
            permission={PAYMENT_VALIDATION_PERMISSIONS.refund}
          >
            <Button
              size="small"
              type="text"
              status="danger"
              icon={<RotateCcw size={15} />}
              style={{ padding: "0 4px" }}
              disabled={
                item.record_type === "refund" ||
                !runtime.result?.order.bank_card_no ||
                refundableAmount(item.amount, item.refunded_amount) <= 0
              }
              onClick={() => runtime.openRefund(item)}
            >
              退款
            </Button>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <>
      <section className="payment-validation-search" aria-label="银行卡号收款查询">
        <Input
          aria-label="团编号"
          allowClear
          placeholder="请输入团编号"
          value={runtime.teamId}
          onChange={runtime.setTeamId}
          onPressEnter={() => void runtime.handleSearch()}
        />
        <PermissionGate
          user={currentUserQuery.data}
          permission={PAYMENT_VALIDATION_PERMISSIONS.create}
          fallback={<Button disabled>查询</Button>}
        >
          <Button
            type="primary"
            icon={<Search size={16} />}
            loading={runtime.loading}
            onClick={() => void runtime.handleSearch()}
          >
            查询
          </Button>
        </PermissionGate>
      </section>

      {runtime.errorMessage ? (
        <div className="payment-validation-alert">
          <Alert type="error" content={runtime.errorMessage} />
        </div>
      ) : null}

      {runtime.result ? (
        <>
          <section className="payment-validation-summary" aria-label="收款订单摘要">
            <div className="payment-validation-amounts">
              <div className="payment-validation-amounts__meta">
                <div>
                  <span>团编号</span>
                  <strong>{runtime.result.order.team_id}</strong>
                </div>
                <div>
                  <span>订单状态</span>
                  <Tag color={statusColor(runtime.result.order.status)}>
                    {formatPaymentStatus(runtime.result.order.status)}
                  </Tag>
                </div>
                <div>
                  <span>申请流水号</span>
                  <strong>{runtime.result.order.receipt_request_id}</strong>
                </div>
              </div>
              <div className="payment-validation-amounts__money" aria-label="收款金额对照">
                <div className="payment-validation-amount payment-validation-amount--expected">
                  <span>应收款</span>
                  <strong>{formatPaymentAmount(runtime.result.order.expected_amount)}</strong>
                </div>
                <div className="payment-validation-amount payment-validation-amount--actual">
                  <span>实际收款</span>
                  <strong>{formatPaymentAmount(runtime.result.order.actual_amount)}</strong>
                </div>
                <div className="payment-validation-amount payment-validation-amount--unpaid">
                  <span>未收款</span>
                  <strong>{formatPaymentAmount(runtime.result.order.unpaid_amount)}</strong>
                </div>
              </div>
            </div>
            <BankCardCollectionInstrument order={runtime.result.order} />
          </section>

          <section className="payment-validation-records" aria-label="付款记录">
            <div className="payment-validation-records__header">
              <Typography.Title heading={6}>付款记录</Typography.Title>
              <Typography.Text type="secondary">
                共 {runtime.result.payments.total} 条
              </Typography.Text>
            </div>
            <Table<PaymentRecord>
              rowKey="id"
              columns={columns}
              data={runtime.result.payments.items ?? []}
              pagination={false}
              scroll={{ x: 1090 }}
              noDataElement="暂无付款记录"
            />
            {runtime.result.payments.total > runtime.pageSize ? (
              <div className="payment-validation-pagination">
                <Pagination
                  current={runtime.page}
                  pageSize={runtime.pageSize}
                  total={runtime.result.payments.total}
                  onChange={(nextPage) => void runtime.handlePageChange(nextPage)}
                />
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      <Drawer
        title={
          runtime.detail?.payment.record_type === "refund" ? "退款详情" : "付款详情"
        }
        aria-label={
          runtime.detail?.payment.record_type === "refund" ? "退款详情" : "付款详情"
        }
        width="min(560px, 100vw)"
        visible={runtime.detailVisible}
        footer={
          <Button long onClick={runtime.closeDetail}>
            关闭
          </Button>
        }
        onCancel={runtime.closeDetail}
      >
        {runtime.detailLoading ? (
          <Spin block />
        ) : runtime.detail ? (
          <PaymentLedgerDetailView detail={runtime.detail} />
        ) : null}
      </Drawer>

      <Modal
        title="提交退款"
        style={{ width: "min(520px, calc(100vw - 24px))" }}
        visible={Boolean(runtime.refundPayment)}
        okText="确认退款"
        cancelText="取消"
        confirmLoading={runtime.refundLoading}
        onOk={() => void runtime.submitRefund()}
        onCancel={runtime.closeRefund}
      >
        {runtime.refundPayment ? (
          <div className="payment-validation-refund">
            <div>
              可退金额：
              <strong>
                {formatPaymentAmount(
                  refundableAmount(
                    runtime.refundPayment.amount,
                    runtime.refundPayment.refunded_amount,
                  ),
                )}
              </strong>
            </div>
            <InputNumber
              aria-label="退款金额"
              min={0.01}
              precision={2}
              step={0.01}
              value={runtime.refundAmount}
              onChange={runtime.setRefundAmount}
              prefix="¥"
              style={{ width: "100%" }}
            />
            {runtime.refundErrorMessage ? (
              <Alert type="error" content={runtime.refundErrorMessage} />
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

/** 渲染卡支付账户、转账与付款记录。 */
function CardPaymentValidationPane() {
  const runtime = useCardPaymentRuntime();
  const currentUserQuery = useCurrentAuthUser();

  const columns: TableColumnProps<CardPaymentRecordItem>[] = [
    {
      title: "流水号",
      dataIndex: "provider_payment_no",
      width: 220,
    },
    {
      title: "交易方式",
      render: (_, item) => <TransactionMethodTag record={item} />,
      width: 140,
    },
    {
      title: "金额",
      render: (_, item) =>
        `${item.record_type === "refund" ? "-" : ""}${Number(item.amount).toFixed(2)} 元`,
      width: 130,
    },
    {
      title: "状态",
      render: (_, item) => (
        <Tag color={statusColor(item.status)}>{formatPaymentStatus(item.status)}</Tag>
      ),
      width: 130,
    },
    {
      title: "提交时间",
      render: (_, item) => formatDateTime(item.paid_at || item.created_at),
      width: 180,
    },
    {
      title: "操作",
      fixed: "right",
      width: 280,
      render: (_, item) => (
        <Space size={4}>
          <Button
            size="small"
            type="text"
            icon={<Eye size={15} />}
            style={{ padding: "0 4px" }}
            onClick={() => runtime.openDetail(item)}
          >
            详细
          </Button>
          {item.record_type !== "refund" ? (
            <>
              <Button
                size="small"
                type="text"
                style={{ padding: "0 4px" }}
                loading={runtime.eReceiptBusyAction === "apply"}
                onClick={() => void runtime.applyEReceipt(item)}
              >
                申请回单
              </Button>
              <Button
                size="small"
                type="text"
                style={{ padding: "0 4px" }}
                loading={runtime.eReceiptBusyAction === "query"}
                onClick={() => void runtime.queryEReceipt(item)}
              >
                查询回单
              </Button>
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <>
      <section className="payment-validation-bank-toolbar" aria-label="卡支付操作">
        <Space>
          <Select
            aria-label="付款账号"
            placeholder="选择付款账号"
            style={{ minWidth: 280 }}
            value={runtime.selectedAccountNo || undefined}
            onChange={(value) => {
              if (typeof value === "string") {
                void runtime.handleSelectAccount(value);
              }
            }}
          >
            {(runtime.result?.accounts ?? []).map((account) => (
              <Select.Option key={account.account_no} value={account.account_no}>
                {account.account_name}（{formatBankCardNumber(account.account_no)}）
              </Select.Option>
            ))}
          </Select>
          <PermissionGate
            user={currentUserQuery.data}
            permission={PAYMENT_VALIDATION_PERMISSIONS.create}
            fallback={<Button disabled>新增卡号</Button>}
          >
            <Button onClick={runtime.openAddAccount}>新增卡号</Button>
          </PermissionGate>
          <PermissionGate
            user={currentUserQuery.data}
            permission={PAYMENT_VALIDATION_PERMISSIONS.access}
            fallback={<Button disabled>刷新</Button>}
          >
            <Button
              icon={<Search size={16} />}
              loading={runtime.loading}
              onClick={() => void runtime.handleRefresh()}
            >
              刷新
            </Button>
          </PermissionGate>
          <PermissionGate
            user={currentUserQuery.data}
            permission={PAYMENT_VALIDATION_PERMISSIONS.create}
            fallback={<Button type="primary" disabled>付款</Button>}
          >
            <Button type="primary" onClick={runtime.openTransfer}>
              付款
            </Button>
          </PermissionGate>
        </Space>
      </section>

      {runtime.errorMessage ? (
        <div className="payment-validation-alert">
          <Alert type="error" content={runtime.errorMessage} />
        </div>
      ) : null}

      {runtime.loading && !runtime.result ? (
        <div className="payment-validation-alert">
          <Spin tip="正在加载付款账户..." />
        </div>
      ) : null}

      {runtime.result ? (
        <>
          <section className="payment-validation-bank-account" aria-label="付款账户摘要">
            <div className="payment-validation-bank-account__name">
              <span>名称</span>
              <strong>{runtime.result.account.account_name}</strong>
            </div>
            <div className="payment-validation-bank-account__meta">
              <div>
                <span>账号</span>
                <strong>{formatBankCardNumber(runtime.result.account.account_no)}</strong>
              </div>
              <div>
                <span>银行</span>
                <strong>{runtime.result.account.bank_name}</strong>
              </div>
            </div>
            <div className="payment-validation-bank-account__cards">
              <div>
                <span>开户网点</span>
                <strong>{runtime.result.account.open_branch_name || "—"}</strong>
              </div>
              <div className="payment-validation-bank-account__total">
                <span>总付款金额</span>
                <strong>{formatPaymentAmount(runtime.result.account.total_paid_amount)}</strong>
              </div>
            </div>
            <Typography.Text type="secondary">
              收款卡号与户名在付款时填写
            </Typography.Text>
          </section>

          <section className="payment-validation-records" aria-label="交易流水">
            <div className="payment-validation-records__header">
              <Typography.Title heading={6}>交易流水</Typography.Title>
              <Typography.Text type="secondary">
                共 {runtime.result.payments.total} 条（含付款与退款）
              </Typography.Text>
            </div>
            <Table<CardPaymentRecordItem>
              rowKey="id"
              columns={columns}
              data={runtime.result.payments.items ?? []}
              pagination={false}
              scroll={{ x: 1200 }}
              noDataElement="暂无交易流水"
            />
            {runtime.result.payments.total > runtime.pageSize ? (
              <div className="payment-validation-pagination">
                <Pagination
                  current={runtime.page}
                  pageSize={runtime.pageSize}
                  total={runtime.result.payments.total}
                  onChange={(nextPage) => void runtime.handlePageChange(nextPage)}
                />
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      <Modal
        title="卡支付转账"
        visible={runtime.transferVisible}
        okText="转账"
        cancelText="取消"
        confirmLoading={runtime.transferring}
        onOk={() => void runtime.submitTransfer()}
        onCancel={runtime.closeTransfer}
      >
        <div className="payment-validation-transfer-form">
          <div aria-label="付款种类">
            <Typography.Text style={{ marginRight: 12 }}>付款种类</Typography.Text>
            <Radio.Group
              value={runtime.transferPayAccountType}
              onChange={(value) => {
                if (value === "00" || value === "01") {
                  runtime.setTransferPayAccountType(value);
                }
              }}
            >
              <Radio value="00">对公</Radio>
              <Radio value="01">对私</Radio>
            </Radio.Group>
          </div>
          <Select
            aria-label="开户行"
            allowClear
            showSearch
            placeholder="请选择开户行"
            value={runtime.transferReceiveOpenBankName || undefined}
            onChange={(value) =>
              runtime.setTransferReceiveOpenBankName(typeof value === "string" ? value : "")
            }
          >
            {CARD_PAYMENT_RECEIVE_BANKS.map((bankName) => (
              <Select.Option key={bankName} value={bankName}>
                {bankName}
              </Select.Option>
            ))}
          </Select>
          <Input
            aria-label="收款卡号"
            allowClear
            placeholder="请输入收款卡号"
            value={runtime.transferReceiveAccountNo}
            onChange={(value) => runtime.setTransferReceiveAccountNo(value)}
          />
          <Input
            aria-label="收款户名"
            allowClear
            placeholder="请输入收款户名"
            value={runtime.transferReceiveAccountName}
            onChange={(value) => runtime.setTransferReceiveAccountName(value)}
          />
          <InputNumber
            aria-label="付款金额"
            placeholder="请输入付款金额"
            min={0.01}
            precision={2}
            prefix="¥"
            style={{ width: "100%" }}
            value={runtime.transferAmount}
            onChange={(value) =>
              runtime.setTransferAmount(typeof value === "number" ? value : undefined)
            }
          />
          {runtime.transferErrorMessage ? (
            <Alert type="error" content={runtime.transferErrorMessage} />
          ) : null}
        </div>
      </Modal>

      <Modal
        title="新增付款卡号"
        visible={runtime.addAccountVisible}
        okText="保存"
        cancelText="取消"
        confirmLoading={runtime.addingAccount}
        onOk={() => void runtime.submitAddAccount()}
        onCancel={runtime.closeAddAccount}
      >
        <div className="payment-validation-transfer-form">
          <Input
            aria-label="账号"
            allowClear
            placeholder="请输入账号"
            value={runtime.addAccountNo}
            onChange={(value) => runtime.setAddAccountNo(value)}
          />
          <Input
            aria-label="名称"
            allowClear
            placeholder="请输入名称"
            value={runtime.addAccountName}
            onChange={(value) => runtime.setAddAccountName(value)}
          />
          <Input
            aria-label="银行"
            allowClear
            placeholder="请输入银行"
            value={runtime.addBankName}
            onChange={(value) => runtime.setAddBankName(value)}
          />
          <Input
            aria-label="开户网点"
            allowClear
            placeholder="请输入开户网点"
            value={runtime.addOpenBranchName}
            onChange={(value) => runtime.setAddOpenBranchName(value)}
          />
          {runtime.addAccountErrorMessage ? (
            <Alert type="error" content={runtime.addAccountErrorMessage} />
          ) : null}
        </div>
      </Modal>

      <Modal
        title="电子回单"
        visible={runtime.eReceiptVisible}
        onCancel={runtime.closeEReceipt}
        footer={
          <Button onClick={runtime.closeEReceipt}>关闭</Button>
        }
        style={{ width: 720 }}
      >
        <div className="payment-validation-transfer-form">
          {runtime.eReceiptErrorMessage ? (
            <Alert type="warning" content={runtime.eReceiptErrorMessage} />
          ) : null}
          <Table
            rowKey="rcpt_num"
            pagination={false}
            data={runtime.eReceiptItems}
            noDataElement="暂无回单"
            columns={[
              { title: "回单编号", dataIndex: "rcpt_num", width: 180 },
              { title: "日期", dataIndex: "date", width: 110 },
              { title: "金额", dataIndex: "amount", width: 100 },
              {
                title: "操作",
                width: 100,
                render: (_, item) => (
                  <Button
                    size="small"
                    type="text"
                    loading={
                      runtime.eReceiptBusyAction === "download" &&
                      runtime.downloadingRcptNum === item.rcpt_num
                    }
                    onClick={() => void runtime.downloadEReceipt(item.rcpt_num)}
                  >
                    下载
                  </Button>
                ),
              },
            ]}
          />
        </div>
      </Modal>

      <Drawer
        title="付款详情"
        aria-label="付款详情"
        width="min(560px, 100vw)"
        visible={runtime.detailVisible}
        footer={
          <Button long onClick={runtime.closeDetail}>
            关闭
          </Button>
        }
        onCancel={runtime.closeDetail}
      >
        {runtime.detailItem ? (
          <div className="payment-validation-detail">
            <Descriptions
              column={1}
              border
              data={[
                {
                  label: runtime.detailItem.record_type === "refund" ? "退款流水号" : "付款流水号",
                  value: runtime.detailItem.provider_payment_no,
                },
                {
                  label: "交易方式",
                  value:
                    runtime.detailItem.record_type === "refund"
                      ? "退款"
                      : runtime.detailItem.transaction_name || "-",
                },
                {
                  label: runtime.detailItem.record_type === "refund" ? "退款金额" : "付款金额",
                  value: formatPaymentAmount(runtime.detailItem.amount),
                },
                {
                  label: "状态",
                  value: formatPaymentStatus(runtime.detailItem.status),
                },
                {
                  label: "提交时间",
                  value: formatDateTime(
                    runtime.detailItem.paid_at || runtime.detailItem.created_at,
                  ),
                },
              ]}
            />
            <Typography.Title heading={6}>渠道返回字段</Typography.Title>
            <dl className="payment-validation-provider-fields">
              {Object.entries(runtime.detailItem.provider_detail ?? {}).map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{formatProviderValue(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </Drawer>
    </>
  );
}

/** 展示中信收款卡号或申请中提示。 */
function BankCardCollectionInstrument({ order }: { order: BankCardCollectionOrder }) {
  if (order.apply_message || !order.bank_card_no) {
    return (
      <div className="payment-validation-bank-card" aria-label="虚拟收款卡申请状态">
        <Alert
          type={order.status === "APPLY_FAILED" ? "error" : "warning"}
          content={order.apply_message || "虚拟收款卡号正在申请中"}
        />
        {order.account_name ? (
          <Typography.Text type="secondary">收款账户：{order.account_name}</Typography.Text>
        ) : null}
      </div>
    );
  }
  return (
    <div className="payment-validation-bank-card" aria-label="虚拟收款卡号">
      <div>
        <span>虚拟收款卡号</span>
        <div className="payment-validation-bank-card__value">
          <strong>{formatBankCardNumber(order.bank_card_no)}</strong>
          <CopyableFieldButton label="虚拟收款卡号" value={order.bank_card_no.replace(/\s/g, "")} />
        </div>
      </div>
      <div>
        <span>开户行</span>
        <div className="payment-validation-bank-card__value">
          <strong>{order.bank_name}</strong>
          <CopyableFieldButton label="开户行" value={order.bank_name} />
        </div>
      </div>
      <div>
        <span>账户名称</span>
        <div className="payment-validation-bank-card__value">
          <strong>{order.account_name || "-"}</strong>
          {order.account_name ? (
            <CopyableFieldButton label="账户名称" value={order.account_name} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** 复制收款字段到剪贴板。 */
function CopyableFieldButton({ label, value }: { label: string; value: string }) {
  return (
    <Button
      type="text"
      size="mini"
      className="payment-validation-copy-button"
      aria-label={`复制${label}`}
      icon={<Copy size={14} />}
      onClick={() => {
        void writeClipboardText(value).then(
          () => Message.success(`${label}已复制`),
          () => Message.error("复制失败，请手动复制"),
        );
      }}
    />
  );
}

/** 优先使用 Clipboard API，不可用或被拒绝时回退到浏览器复制命令。 */
async function writeClipboardText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // 权限拒绝时继续使用兼容路径。
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.readOnly = true;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  try {
    textarea.select();
    if (!document.execCommand?.("copy")) {
      throw new Error("当前浏览器不支持自动复制");
    }
  } finally {
    textarea.remove();
  }
}

/** 展示银联二维码收款工具。 */
function QrCollectionInstrument({ order }: { order: QrCollectionOrder }) {
  return (
    <div className="payment-validation-qr">
      <div role="img" aria-label={`${order.team_id} 收款二维码`}>
        <QRCode value={order.qr_code_url} size={208} />
      </div>
      <Typography.Text type="secondary">请使用支持银联支付的应用扫码</Typography.Text>
    </div>
  );
}

/** 展示付款或退款详情，以及渠道回包字段。 */
function PaymentLedgerDetailView({ detail }: { detail: PaymentRecordDetail }) {
  const isRefund = detail.payment.record_type === "refund";
  return (
    <div className="payment-validation-detail">
      <Descriptions
        column={1}
        border
        data={[
          {
            label: isRefund ? "退款流水号" : "付款流水号",
            value: detail.payment.provider_payment_no,
          },
          {
            label: "交易方式",
            value: isRefund ? "退款" : detail.payment.transaction_name || "-",
          },
          {
            label: isRefund ? "退款金额" : "付款金额",
            value: formatPaymentAmount(detail.payment.amount),
          },
          { label: "状态", value: formatPaymentStatus(detail.payment.status) },
          {
            label: isRefund ? "提交时间" : "付款时间",
            value: formatDateTime(detail.payment.paid_at || detail.payment.created_at),
          },
        ]}
      />
      {!isRefund && detail.refunds.length > 0 ? (
        <>
          <Typography.Title heading={6}>关联退款</Typography.Title>
          <ul className="payment-validation-refund-list">
            {detail.refunds.map((item) => (
              <li key={item.id}>
                {item.merchant_refund_order_id} · {formatPaymentAmount(item.amount)} ·{" "}
                {formatPaymentStatus(item.status)}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <Typography.Title heading={6}>渠道返回字段</Typography.Title>
      <dl className="payment-validation-provider-fields">
        {Object.entries(detail.provider_detail ?? {}).map(([key, value]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd>{formatProviderValue(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** 返回渠道状态标签颜色。 */
function statusColor(status: string): string {
  if (status === "SUCCESS") return "green";
  if (status === "FAIL" || status === "REFUND_FAIL" || status === "APPLY_FAILED") return "red";
  if (
    status === "PART_PAID" ||
    status === "PROCESSING" ||
    status === "PENDING_REVIEW" ||
    status === "APPLYING"
  ) {
    return "orange";
  }
  return "arcoblue";
}

/** 交易方式：退款红色、转账/付款绿色。 */
function TransactionMethodTag({
  record,
}: {
  record: Pick<PaymentRecord, "record_type" | "transaction_name" | "transaction_type">;
}) {
  const isRefund = record.record_type === "refund";
  const label = isRefund
    ? "退款"
    : record.transaction_name || record.transaction_type || "-";
  if (label === "-") {
    return <>-</>;
  }
  return <Tag color={isRefund ? "red" : "green"}>{label}</Tag>;
}

/** 把接口时间格式化为表格展示值。 */
function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 19);
}

/** 将收款账号按四位分组展示。 */
function formatBankCardNumber(value: string): string {
  return value.replace(/\s/g, "").match(/.{1,4}/g)?.join(" ") ?? value;
}

/** 把渠道详情字段转换为可读文本。 */
function formatProviderValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
